import {
  CheckCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExportOutlined,
  ImportOutlined,
  PlusOutlined,
  SaveOutlined,
  SendOutlined,
  SwapOutlined,
} from '@ant-design/icons'
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import {
  Alert,
  Button,
  Collapse,
  Divider,
  Input,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from '@surecompanies/core_components'
import { useQuery } from '@tanstack/react-query'
import epsClient from 'enrollment-platform/api/eps.client'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import Page from 'layouts/Page'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { browserHistory } from 'react-router'
import CensusOnboardingFileDropzone from '../../components/CensusOnboardingFileDropzone'
import { buildApiIntegrationDefaultRules } from '../../models/apiIntegrationDefaultRules'
import {
  TEMPLATE_TYPE_LABELS,
  TemplateType,
  isTemplateType,
} from '../../models/templateType'
import { CENSUS_ONBOARDING, CENSUS_TEMPLATE_CREATE } from '../../paths'
import ColumnCard from './components/ColumnCard'
import SortableColumnCard from './components/SortableColumnCard'
import styles from './CreateCensusTemplate.module.scss'
import useActiveHrisTemplatesForImport from './hooks/useActiveHrisTemplatesForImport'
import useComputedFields from './hooks/useComputedFields'
import useFileUpload from './hooks/useFileUpload'
import useMappingRules from './hooks/useMappingRules'
import usePreviewData from './hooks/usePreviewData'
import useSourceTemplate from './hooks/useSourceTemplate'
import { ConditionType } from './types'
import {
  buildMappingRulesExportFilename,
  downloadJsonBlob,
} from './utils/exportCsv'
import {
  applyComputedFieldsToRows,
  exportRulesJSON,
  importRulesJSON,
  validateMappingRules,
} from './utils/transformer'

interface CreateCensusTemplateProps {
  params?: { id: string; templateId?: string }
  location?: { query?: { from?: string; type?: string }; pathname?: string }
  route?: { path?: string }
}

/** Drop names that are already listed under computedFieldNames to avoid duplicates. */
function withoutColumns(columns: string[], exclude: string[]): string[] {
  if (exclude.length === 0) return columns
  const excluded = new Set(exclude)
  return columns.filter((c) => !excluded.has(c))
}

export default function CreateCensusTemplate({
  params,
  location,
  route,
}: CreateCensusTemplateProps) {
  const customerId = params?.id ?? ''
  const templateId = params?.templateId

  const mode: 'create' | 'edit' | 'view' = (() => {
    const routePath = route?.path ?? ''
    const urlPath = location?.pathname ?? window.location.pathname
    if (
      routePath.includes('/view/') ||
      urlPath.includes('/census-template/view/')
    ) {
      return 'view'
    }
    if (templateId) return 'edit'
    return 'create'
  })()
  const isViewOnly = mode === 'view'

  const sourceTemplateId =
    mode === 'create' ? location?.query?.from : templateId

  const queryType = location?.query?.type
  const urlType: TemplateType | null = isTemplateType(queryType)
    ? queryType
    : null

  const { data: customer } = useQuery({
    queryKey: ['getCustomer', customerId],
    queryFn: async () => {
      const result = await epsClient.get(`/customers/${customerId}`)
      return result.data
    },
    enabled: !!customerId,
  })
  const clientName = (customer as { name?: string })?.name ?? customerId

  const {
    mappingRules,
    setMappingRules,
    movedCardId,
    lastAddedId,
    addRule,
    updateRule,
    removeRule,
    reorderRules,
    resetRules,
  } = useMappingRules()

  const {
    computedFields,
    setComputedFields,
    movedComputedFieldId,
    lastAddedComputedFieldId,
    addComputedField,
    updateComputedField,
    removeComputedField,
    reorderComputedFields,
    resetComputedFields,
  } = useComputedFields()

  const importInputRef = useRef<HTMLInputElement>(null)

  const sortableIds = useMemo(
    () => mappingRules.map((r) => r.id),
    [mappingRules]
  )

  const sortableComputedFieldIds = useMemo(
    () => computedFields.map((r) => r.id),
    [computedFields]
  )

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor)
  )

  const handleMappingRulesDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      reorderRules(String(active.id), String(over.id))
    },
    [reorderRules]
  )

  const handleComputedFieldsDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      reorderComputedFields(String(active.id), String(over.id))
    },
    [reorderComputedFields]
  )

  useEffect(() => {
    if (!lastAddedId) return
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-rule-id="${lastAddedId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }, [lastAddedId])

  useEffect(() => {
    if (!lastAddedComputedFieldId) return
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-rule-id="${lastAddedComputedFieldId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }, [lastAddedComputedFieldId])

  const { sourceTemplate } = useSourceTemplate(
    customerId,
    sourceTemplateId,
    setMappingRules,
    setComputedFields
  )

  const activeType: TemplateType | null = useMemo(() => {
    if (mode === 'create') return urlType
    return (sourceTemplate?.type as TemplateType | undefined) ?? null
  }, [mode, urlType, sourceTemplate?.type])

  const typeLabel = activeType ? TEMPLATE_TYPE_LABELS[activeType] : null

  // Auto-fill the rules grid with the person-schema passthrough baseline
  // (44 fields filtered from the upstream emit set) when creating a fresh
  // API_INTEGRATION template (no `?from=` clone source). Authors can then
  // customize the handful of fields that need transforms.
  //
  // We guard with a ref so the auto-fill runs at most once per mount.
  // Without this, clearing the grid (e.g. "Reset all changes" or removing
  // every column) would trip `mappingRules.length === 0` and silently
  // repopulate the rules, defeating the user's intent.
  const didAutoFillApiIntegrationRef = useRef(false)
  useEffect(() => {
    if (didAutoFillApiIntegrationRef.current) return
    if (mode !== 'create') return
    if (activeType !== 'API_INTEGRATION') return
    if (sourceTemplateId) return
    if (mappingRules.length > 0) return
    didAutoFillApiIntegrationRef.current = true
    setMappingRules(buildApiIntegrationDefaultRules())
  }, [mode, activeType, sourceTemplateId, mappingRules.length, setMappingRules])

  const onRulesGenerated = useCallback(
    (rules: typeof mappingRules) => {
      setMappingRules((prev) => (prev.length > 0 ? prev : rules))
    },
    [setMappingRules]
  )

  const {
    parsedFile,
    sourceColumns: fileSourceColumns,
    uploadFile,
    resetFile,
  } = useFileUpload(onRulesGenerated)

  // All selectable source names (file headers + any columns already referenced
  // by rules). Computed-field targets that collide with a file column are kept
  // here; callers exclude only the targets they also offer under
  // computedFieldNames so earlier computed cards can still pick the original
  // file column when a later computed field reuses that name.
  const allSourceColumns = useMemo(() => {
    const cols = new Set(fileSourceColumns)
    for (const rule of [...computedFields, ...mappingRules]) {
      if (rule.source) cols.add(rule.source)
      if (rule.sourceField) cols.add(rule.sourceField)
      if (rule.sources) rule.sources.forEach((s) => cols.add(s))
      if (rule.sourceEntries) {
        rule.sourceEntries.forEach((e) => cols.add(e.source))
      }
    }
    return Array.from(cols)
  }, [fileSourceColumns, mappingRules, computedFields])

  const computedFieldNames = useMemo(
    () => computedFields.map((r) => r.target).filter(Boolean),
    [computedFields]
  )

  // Logic Designer: omit names offered separately as Computed Fields.
  const sourceColumns = useMemo(
    () => withoutColumns(allSourceColumns, computedFieldNames),
    [allSourceColumns, computedFieldNames]
  )

  // Unique source values for lookup/contains suggestions. Rows are enriched
  // with computed-field outputs so suggestions match working-row values used
  // during transform (including when a computed target shadows a file column).
  const sourceValuesByColumn = useMemo(() => {
    const map: Record<string, Set<string>> = {}
    const ensureSet = (col: string) => {
      if (!map[col]) map[col] = new Set<string>()
      return map[col]
    }

    if (parsedFile?.rows) {
      const rows = applyComputedFieldsToRows(parsedFile.rows, computedFields)
      for (const col of allSourceColumns) {
        const set = ensureSet(col)
        for (const row of rows) {
          const val = row[col]
          if (val != null && val !== '') set.add(val)
        }
      }
    }

    for (const rule of [...computedFields, ...mappingRules]) {
      if (rule.condition === ConditionType.LOOKUP_MAP && rule.mapping) {
        const cols = rule.source ? [rule.source] : (rule.sources ?? [])
        for (const col of cols) {
          if (!col) continue
          const set = ensureSet(col)
          for (const key of Object.keys(rule.mapping)) {
            if (key !== '') set.add(key)
          }
        }
      }
    }

    const result: Record<string, string[]> = {}
    for (const [col, set] of Object.entries(map)) {
      result[col] = Array.from(set).sort()
    }
    return result
  }, [parsedFile?.rows, allSourceColumns, mappingRules, computedFields])

  const {
    previewEnabled,
    setPreviewEnabled,
    previewSourceData,
    previewTransformedData,
    sourceTableColumns,
    transformedTableColumns,
    handleExportResult,
    showPreview,
    previewRowLimit,
  } = usePreviewData(parsedFile, mappingRules, computedFields)

  const handleReset = useCallback(() => {
    Modal.confirm({
      title: 'Reset all changes',
      content:
        'This will clear all mapping rules, computed fields, and the uploaded file. Are you sure?',
      okText: 'Reset',
      okButtonProps: { danger: true },
      onOk: () => {
        resetFile()
        resetRules()
        resetComputedFields()
      },
    })
  }, [resetFile, resetRules, resetComputedFields])

  const [activating, setActivating] = useState(false)

  const handleActivate = useCallback(async () => {
    if (!templateId) return
    Modal.confirm({
      title: 'Activate Template',
      content:
        'This will activate this template and deactivate the current active version. Are you sure?',
      okText: 'Activate',
      okButtonProps: { danger: true },
      onOk: async () => {
        setActivating(true)
        try {
          await epsClient.post(
            `/api/v1/hris-mapping-templates/${templateId}/activate`
          )
          message.success('Template activated successfully')
          browserHistory.push(`/${CENSUS_ONBOARDING}/${customerId}`)
        } catch {
          message.error('Failed to activate template')
        } finally {
          setActivating(false)
        }
      },
    })
  }, [templateId, customerId])

  const [archiving, setArchiving] = useState(false)

  const handleArchive = useCallback(async () => {
    if (!templateId) return
    Modal.confirm({
      title: 'Archive Template',
      content:
        'This will archive this template. It will no longer be available for activation. Are you sure?',
      okText: 'Archive',
      okButtonProps: { danger: true },
      onOk: async () => {
        setArchiving(true)
        try {
          await epsClient.patch(
            `/api/v1/hris-mapping-templates/${templateId}/archive`
          )
          message.success('Template archived successfully')
          browserHistory.push(`/${CENSUS_ONBOARDING}/${customerId}`)
        } catch {
          message.error('Failed to archive template')
        } finally {
          setArchiving(false)
        }
      },
    })
  }, [templateId, customerId])

  const isViewSourceActive = useMemo(
    () => (sourceTemplate?.status ?? '').trim().toUpperCase() === 'ACTIVE',
    [sourceTemplate?.status]
  )

  const isDraftTemplateStatus = useMemo(
    () => (sourceTemplate?.status ?? '').trim().toUpperCase() === 'DRAFT',
    [sourceTemplate?.status]
  )

  const handleCreateNewVersionFromView = useCallback(() => {
    if (!templateId) return
    const typeParam = activeType ? `&type=${activeType}` : ''
    browserHistory.push(
      `/${CENSUS_ONBOARDING}/${customerId}/${CENSUS_TEMPLATE_CREATE}?from=${templateId}${typeParam}`
    )
  }, [customerId, templateId, activeType])

  const [saving, setSaving] = useState(false)
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [saveModalAction, setSaveModalAction] = useState<'DRAFT' | 'PUBLISHED'>(
    'DRAFT'
  )
  const [templateName, setTemplateName] = useState('')
  const [templateDescription, setTemplateDescription] = useState('')
  const [changeSummary, setChangeSummary] = useState('')
  const mappingRuleValidationErrors = useMemo(
    () => validateMappingRules(mappingRules, computedFields),
    [mappingRules, computedFields]
  )
  const hasMappingRuleValidationErrors = mappingRuleValidationErrors.length > 0

  useEffect(() => {
    if (mode !== 'edit' || !sourceTemplate) return
    if (sourceTemplate.name) setTemplateName(sourceTemplate.name)
    if (sourceTemplate.description)
      setTemplateDescription(sourceTemplate.description)
    if (sourceTemplate.changeSummary)
      setChangeSummary(sourceTemplate.changeSummary)
  }, [mode, sourceTemplate])

  const handleSubmitSave = useCallback(async () => {
    if (!templateName.trim()) {
      message.warning('Template name is required.')
      return
    }
    if (mode === 'create' && !activeType) {
      message.warning('Template type is required.')
      return
    }
    if (mappingRules.length === 0) {
      message.warning('Add at least one mapping rule before saving.')
      return
    }
    const latestMappingRuleValidationErrors = validateMappingRules(
      mappingRules,
      computedFields
    )
    if (latestMappingRuleValidationErrors.length > 0) {
      message.warning(latestMappingRuleValidationErrors[0])
      return
    }
    setSaving(true)
    try {
      const payload = {
        clientPublicKey: customerId,
        name: templateName.trim(),
        description: templateDescription.trim() || undefined,
        changeSummary: changeSummary.trim() || undefined,
        mappingRules: JSON.parse(exportRulesJSON(mappingRules, computedFields)),
        ...(mode === 'create' && activeType ? { type: activeType } : {}),
      }
      if (mode === 'edit' && templateId) {
        if (saveModalAction === 'DRAFT') {
          await epsClient.patch(
            `/api/v1/hris-mapping-templates/${templateId}`,
            payload
          )
        } else {
          await epsClient.patch(
            `/api/v1/hris-mapping-templates/${templateId}/publish`,
            payload
          )
        }
      } else if (saveModalAction === 'PUBLISHED') {
        await epsClient.post('/api/v1/hris-mapping-templates/publish', payload)
      } else {
        await epsClient.post('/api/v1/hris-mapping-templates', payload)
      }
      message.success(
        mode === 'edit' && saveModalAction === 'PUBLISHED'
          ? 'Template published successfully'
          : mode === 'edit' && saveModalAction === 'DRAFT'
            ? 'Draft saved successfully'
            : saveModalAction === 'PUBLISHED'
              ? 'Template saved & published successfully'
              : 'Draft saved successfully'
      )
      setSaveModalOpen(false)
      browserHistory.push(`/${CENSUS_ONBOARDING}/${customerId}`)
    } catch {
      message.error(
        mode === 'edit' && saveModalAction === 'PUBLISHED'
          ? 'Failed to publish template'
          : mode === 'edit' && saveModalAction === 'DRAFT'
            ? 'Failed to save draft'
            : saveModalAction === 'PUBLISHED'
              ? 'Failed to save & publish template'
              : 'Failed to save draft'
      )
    } finally {
      setSaving(false)
    }
  }, [
    customerId,
    mappingRules,
    computedFields,
    templateName,
    templateDescription,
    changeSummary,
    saveModalAction,
    mode,
    templateId,
    activeType,
  ])

  const openSaveModal = useCallback(
    (action: 'DRAFT' | 'PUBLISHED') => {
      if (mappingRules.length === 0) {
        message.warning('Add at least one mapping rule before saving.')
        return
      }
      if (hasMappingRuleValidationErrors) {
        message.warning(mappingRuleValidationErrors[0])
        return
      }
      setSaveModalAction(action)
      setSaveModalOpen(true)
    },
    [
      hasMappingRuleValidationErrors,
      mappingRules.length,
      mappingRuleValidationErrors,
    ]
  )

  const handleSaveDraft = useCallback(
    () => openSaveModal('DRAFT'),
    [openSaveModal]
  )

  const handlePublish = useCallback(
    () => openSaveModal('PUBLISHED'),
    [openSaveModal]
  )

  const handleExportRules = useCallback(() => {
    const json = exportRulesJSON(mappingRules, computedFields)
    downloadJsonBlob(json, buildMappingRulesExportFilename(customerId))
  }, [mappingRules, computedFields, customerId])

  const handleImportFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      try {
        const text = await file.text()
        const imported = importRulesJSON(text)
        setMappingRules(imported.mappingRules)
        setComputedFields(imported.computedFields)
        message.success(
          imported.computedFields.length > 0
            ? `Imported ${imported.mappingRules.length} mapping rules and ${imported.computedFields.length} computed fields`
            : `Imported ${imported.mappingRules.length} mapping rules`
        )
      } catch {
        message.error('Failed to parse mapping rules JSON')
      }

      if (importInputRef.current) importInputRef.current.value = ''
    },
    [setMappingRules, setComputedFields]
  )

  const [importRulesModalOpen, setImportRulesModalOpen] = useState(false)
  const [selectedImportTemplateId, setSelectedImportTemplateId] = useState<
    string | undefined
  >()
  const [importingFromCompany, setImportingFromCompany] = useState(false)

  const {
    activeImportTemplates,
    importSelectOptions,
    isLoading: isImportCompaniesLoading,
  } = useActiveHrisTemplatesForImport(importRulesModalOpen)

  const openImportRulesModal = useCallback(() => {
    setSelectedImportTemplateId(undefined)
    setImportRulesModalOpen(true)
  }, [])

  const hasExistingRulesToReplace =
    mappingRules.length > 0 || computedFields.length > 0

  const importReplaceConfirmContent =
    'This will replace all existing mapping rules and computed fields. Are you sure?'

  const handleImportFromFileInModal = useCallback(() => {
    const openFilePicker = () => {
      setImportRulesModalOpen(false)
      requestAnimationFrame(() => importInputRef.current?.click())
    }
    if (!hasExistingRulesToReplace) {
      openFilePicker()
      return
    }
    Modal.confirm({
      title: 'Import rules',
      content: importReplaceConfirmContent,
      okText: 'Continue',
      onOk: openFilePicker,
    })
  }, [importInputRef, hasExistingRulesToReplace])

  const handleImportFromActiveCompanyTemplate = useCallback(async () => {
    if (!selectedImportTemplateId) {
      message.warning('Select a company.')
      return
    }

    const template = activeImportTemplates.find(
      (t) => t.id === selectedImportTemplateId
    )
    if (!template?.mappingRules) {
      message.error('Could not load the selected template.')
      return
    }

    const runImport = async () => {
      setImportingFromCompany(true)
      try {
        const imported = importRulesJSON(JSON.stringify(template.mappingRules))
        setMappingRules(imported.mappingRules)
        setComputedFields(imported.computedFields)
        message.success(
          imported.computedFields.length > 0
            ? `Imported ${imported.mappingRules.length} mapping rules and ${imported.computedFields.length} computed fields`
            : `Imported ${imported.mappingRules.length} mapping rules`
        )
        setImportRulesModalOpen(false)
        setSelectedImportTemplateId(undefined)
      } catch {
        message.error('Failed to import mapping rules')
      } finally {
        setImportingFromCompany(false)
      }
    }

    if (!hasExistingRulesToReplace) {
      await runImport()
      return
    }

    Modal.confirm({
      title: 'Import rules',
      content: importReplaceConfirmContent,
      okText: 'Continue',
      onOk: runImport,
    })
  }, [
    activeImportTemplates,
    hasExistingRulesToReplace,
    selectedImportTemplateId,
    setMappingRules,
    setComputedFields,
  ])

  return (
    <Page
      title=""
      maxWidth="100%"
      breadcrumb={
        <BreadcrumbNav
          items={[
            { title: 'Home', path: '/' },
            {
              title: 'Census Onboarding',
              path: `/${CENSUS_ONBOARDING}`,
            },
            {
              title: clientName,
              path: `/${CENSUS_ONBOARDING}/${customerId}`,
            },
            {
              title:
                mode === 'view'
                  ? 'View Census Template'
                  : mode === 'edit'
                    ? 'Edit Census Template'
                    : 'Create Census Template',
            },
          ]}
        />
      }
    >
      {/* Page header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerTitle}>
          <div className={styles.headerIcon}>
            <SwapOutlined />
          </div>
          <div className={styles.headerInfo}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {mode === 'view'
                ? 'View HRIS Rules'
                : mode === 'edit'
                  ? 'Edit HRIS Rules'
                  : 'HRIS Rules'}
              {typeLabel && (
                <Tag
                  color="#003146"
                  style={{ fontSize: 12, padding: '2px 10px' }}
                >
                  {typeLabel}
                </Tag>
              )}
            </h2>
            <p>Rule-Based Data Transformation</p>
          </div>
        </div>
        {!isViewOnly && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              className={styles.resetLink}
              onClick={handleReset}
              role="button"
              tabIndex={0}
            >
              Reset
            </span>
            {(mode === 'create' ||
              (mode === 'edit' && isDraftTemplateStatus)) && (
              <Button
                icon={<SaveOutlined />}
                onClick={handleSaveDraft}
                loading={saving}
                disabled={
                  mappingRules.length === 0 || hasMappingRuleValidationErrors
                }
              >
                Save Draft
              </Button>
            )}
            {mode === 'create' && (
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handlePublish}
                loading={saving}
                disabled={
                  mappingRules.length === 0 || hasMappingRuleValidationErrors
                }
              >
                Save & Publish
              </Button>
            )}
            {mode === 'edit' && (
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handlePublish}
                loading={saving}
                disabled={
                  mappingRules.length === 0 || hasMappingRuleValidationErrors
                }
              >
                Publish
              </Button>
            )}
          </div>
        )}
        {isViewOnly && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag color="blue" style={{ fontSize: 13, padding: '4px 12px' }}>
              View Only
            </Tag>
            {isViewSourceActive && templateId && (
              <Button
                icon={<PlusOutlined />}
                onClick={handleCreateNewVersionFromView}
              >
                Create new version
              </Button>
            )}
            {sourceTemplate?.status === 'PUBLISHED' && (
              <>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={handleActivate}
                  loading={activating}
                  style={{
                    backgroundColor: '#38a169',
                    borderColor: '#38a169',
                  }}
                >
                  Activate
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleArchive}
                  loading={archiving}
                >
                  Archive
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {mode === 'create' && !activeType && (
        <Alert
          type="error"
          showIcon
          message="Missing template type"
          description={
            <span>
              This page must be opened with a <code>type</code> query parameter
              (e.g. <code>?type=AMA</code> or <code>?type=API_INTEGRATION</code>
              ). Go back to the customer detail page and use the create buttons.
            </span>
          }
          style={{ marginBottom: 16 }}
          action={
            <Button
              size="small"
              onClick={() =>
                browserHistory.push(`/${CENSUS_ONBOARDING}/${customerId}`)
              }
            >
              Back
            </Button>
          }
        />
      )}

      <Collapse
        defaultActiveKey={['source', 'computed', 'logic', 'preview']}
        style={{ background: 'transparent', border: 0 }}
      >
        {/* Section 01: Connect Source */}
        <Collapse.Panel
          key="source"
          header={
            <div className={styles.sectionHeader} style={{ margin: 0 }}>
              <span className={styles.sectionBadge}>01</span>
              <span className={styles.sectionTitle}>Connect Source</span>
              {parsedFile && (
                <Tag color="green" style={{ marginLeft: 8 }}>
                  {parsedFile.fileName}
                </Tag>
              )}
            </div>
          }
          className={styles.connectSourceCard}
        >
          <p className={styles.sectionSubtitle}>
            Import your data file to begin building your transformation pipeline
          </p>

          <CensusOnboardingFileDropzone
            parsedFile={parsedFile}
            onFileSelected={uploadFile}
            emptyHint="Maximum recommended: 50,000 rows"
          />
        </Collapse.Panel>

        {/* Section 02: Computed Fields */}
        <Collapse.Panel
          key="computed"
          header={
            <div
              className={styles.sectionHeader}
              style={{ margin: 0, flex: 1 }}
            >
              <span className={styles.sectionBadge}>02</span>
              <span className={styles.sectionTitle}>
                Computed Fields (Optional)
              </span>
              <Tag className={styles.columnCount} style={{ marginLeft: 8 }}>
                {computedFields.length} FIELDS
              </Tag>
            </div>
          }
          extra={
            !isViewOnly && (
              <div
                className={styles.logicDesignerActions}
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  size="small"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={addComputedField}
                >
                  New Computed Field
                </Button>
              </div>
            )
          }
          className={styles.logicDesignerCard}
        >
          <p className={styles.sectionSubtitle}>
            Intermediate values computed from source columns (or from an earlier
            computed field, in the order below) — reference them from any Logic
            Designer column&apos;s source dropdown.
          </p>
          {computedFields.length === 0 ? (
            <div className={styles.emptyState}>
              No computed fields yet. Click &quot;New Computed Field&quot; to
              derive a value (e.g. annualizing an hourly wage) before mapping it
              into a census column below.
            </div>
          ) : (
            <div className={styles.columnsGrid}>
              {isViewOnly ? (
                computedFields.map((rule, idx) => {
                  // Only earlier computed targets are offered here — exclude
                  // just those from sourceColumns so a later field that reuses
                  // a file column name doesn't hide that original source.
                  const earlierComputedNames = computedFields
                    .slice(0, idx)
                    .map((r) => r.target)
                    .filter(Boolean)
                  return (
                    <div
                      key={rule.id}
                      data-rule-id={rule.id}
                      className={styles.columnsGridItem}
                    >
                      <ColumnCard
                        rule={rule}
                        index={idx}
                        sourceColumns={withoutColumns(
                          allSourceColumns,
                          earlierComputedNames
                        )}
                        computedFieldNames={earlierComputedNames}
                        sourceValuesByColumn={sourceValuesByColumn}
                        onChange={updateComputedField}
                        onRemove={removeComputedField}
                        isMoved={movedComputedFieldId === rule.id}
                        readOnly={isViewOnly}
                      />
                    </div>
                  )
                })
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleComputedFieldsDragEnd}
                >
                  <SortableContext
                    items={sortableComputedFieldIds}
                    strategy={rectSortingStrategy}
                  >
                    {computedFields.map((rule, idx) => {
                      const earlierComputedNames = computedFields
                        .slice(0, idx)
                        .map((r) => r.target)
                        .filter(Boolean)
                      return (
                        <SortableColumnCard
                          key={rule.id}
                          rule={rule}
                          index={idx}
                          sourceColumns={withoutColumns(
                            allSourceColumns,
                            earlierComputedNames
                          )}
                          computedFieldNames={earlierComputedNames}
                          sourceValuesByColumn={sourceValuesByColumn}
                          onChange={updateComputedField}
                          onRemove={removeComputedField}
                          isMoved={movedComputedFieldId === rule.id}
                          readOnly={isViewOnly}
                        />
                      )
                    })}
                  </SortableContext>
                </DndContext>
              )}
            </div>
          )}
        </Collapse.Panel>

        {/* Section 03: Logic Designer */}
        <Collapse.Panel
          key="logic"
          header={
            <div
              className={styles.sectionHeader}
              style={{ margin: 0, flex: 1 }}
            >
              <span className={styles.sectionBadge}>03</span>
              <span className={styles.sectionTitle}>Logic Designer</span>
              <Tag className={styles.columnCount} style={{ marginLeft: 8 }}>
                {mappingRules.length} COLUMNS
              </Tag>
            </div>
          }
          extra={
            <div
              className={styles.logicDesignerActions}
              onClick={(e) => e.stopPropagation()}
            >
              {!isViewOnly && (
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".json"
                  className={styles.hiddenInput}
                  onChange={handleImportFile}
                />
              )}
              {!isViewOnly && (
                <Button
                  size="small"
                  icon={<ImportOutlined />}
                  onClick={openImportRulesModal}
                >
                  Import Rules
                </Button>
              )}
              <Button
                size="small"
                icon={<ExportOutlined />}
                onClick={handleExportRules}
                disabled={
                  mappingRules.length === 0 && computedFields.length === 0
                }
              >
                Export JSON Rules
              </Button>
              {!isViewOnly && (
                <Button
                  size="small"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={addRule}
                >
                  New Column
                </Button>
              )}
            </div>
          }
          className={styles.logicDesignerCard}
        >
          {mappingRules.length === 0 ? (
            <div className={styles.emptyState}>
              No mapping rules yet. Click &quot;New Column&quot; to add your
              first mapping rule, or &quot;Import Rules&quot; to load from a
              JSON file.
            </div>
          ) : (
            <div className={styles.columnsGrid}>
              {isViewOnly ? (
                mappingRules.map((rule, idx) => (
                  <div
                    key={rule.id}
                    data-rule-id={rule.id}
                    className={styles.columnsGridItem}
                  >
                    <ColumnCard
                      rule={rule}
                      index={idx}
                      sourceColumns={sourceColumns}
                      computedFieldNames={computedFieldNames}
                      sourceValuesByColumn={sourceValuesByColumn}
                      onChange={updateRule}
                      onRemove={removeRule}
                      isMoved={movedCardId === rule.id}
                      readOnly={isViewOnly}
                    />
                  </div>
                ))
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleMappingRulesDragEnd}
                >
                  <SortableContext
                    items={sortableIds}
                    strategy={rectSortingStrategy}
                  >
                    {mappingRules.map((rule, idx) => (
                      <SortableColumnCard
                        key={rule.id}
                        rule={rule}
                        index={idx}
                        sourceColumns={sourceColumns}
                        computedFieldNames={computedFieldNames}
                        sourceValuesByColumn={sourceValuesByColumn}
                        onChange={updateRule}
                        onRemove={removeRule}
                        isMoved={movedCardId === rule.id}
                        readOnly={isViewOnly}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>
          )}
        </Collapse.Panel>

        {/* Section 04: Side-by-Side Comparison */}
        <Collapse.Panel
          key="preview"
          header={
            <div
              className={styles.sectionHeader}
              style={{ margin: 0, flex: 1 }}
            >
              <span className={styles.sectionBadge}>04</span>
              <span className={styles.sectionTitle}>
                Side-by-Side Comparison
              </span>
            </div>
          }
          extra={
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              onClick={(e) => e.stopPropagation()}
            >
              <span style={{ fontSize: 13, color: '#677786' }}>PREVIEW</span>
              <Switch
                checked={previewEnabled}
                onChange={setPreviewEnabled}
                size="small"
              />
              <Button
                size="small"
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExportResult}
                disabled={!parsedFile || mappingRules.length === 0}
              >
                Export Result
              </Button>
            </div>
          }
          className={styles.comparisonCard}
        >
          {showPreview ? (
            <>
              <div className={styles.comparisonTables}>
                <div className={styles.comparisonTableWrapper}>
                  <div className={styles.comparisonTableLabel}>
                    ORIGINAL DATA (SOURCE)
                    <span className={styles.labelDots}>
                      <span
                        className={`${styles.labelDot} ${styles.sourceDot}`}
                      />
                      <span
                        className={`${styles.labelDot} ${styles.sourceDot}`}
                      />
                      <span
                        className={`${styles.labelDot} ${styles.sourceDot}`}
                      />
                    </span>
                  </div>
                  <Table
                    dataSource={previewSourceData}
                    columns={sourceTableColumns as any}
                    pagination={false}
                    size="small"
                    scroll={{ x: true }}
                  />
                </div>

                <div className={styles.divider} />

                <div className={styles.comparisonTableWrapper}>
                  <div className={styles.comparisonTableLabel}>
                    TRANSFORMED DATA (OUTPUT)
                    <span className={styles.labelDots}>
                      <span
                        className={`${styles.labelDot} ${styles.transformedDot}`}
                      />
                      <span
                        className={`${styles.labelDot} ${styles.transformedDot}`}
                      />
                      <span
                        className={`${styles.labelDot} ${styles.transformedDot}`}
                      />
                    </span>
                  </div>
                  <Table
                    dataSource={previewTransformedData}
                    columns={transformedTableColumns as any}
                    pagination={false}
                    size="small"
                    scroll={{ x: true }}
                  />
                </div>
              </div>

              <div className={styles.syncFooter}>
                <div className={styles.syncActive}>
                  <span className={styles.syncDot} />
                  LIVE SYNCHRONIZATION ACTIVE
                </div>
                <span className={styles.syncHint}>
                  Only the first {previewRowLimit} rows are shown in preview
                  mode.
                </span>
              </div>
            </>
          ) : (
            <div className={styles.emptyState}>
              {!parsedFile
                ? 'Upload a source file and add mapping rules to see the preview.'
                : 'Add mapping rules to see the transformed preview.'}
            </div>
          )}
        </Collapse.Panel>
      </Collapse>

      <Modal
        title={
          mode === 'edit' && saveModalAction === 'PUBLISHED'
            ? 'Publish Template'
            : saveModalAction === 'PUBLISHED'
              ? 'Save & Publish Template'
              : 'Save Draft'
        }
        open={saveModalOpen}
        onCancel={() => setSaveModalOpen(false)}
        onOk={handleSubmitSave}
        okText={
          mode === 'edit' && saveModalAction === 'PUBLISHED'
            ? 'Publish'
            : saveModalAction === 'PUBLISHED'
              ? 'Save & Publish'
              : 'Save Draft'
        }
        okButtonProps={{
          loading: saving,
          danger: saveModalAction === 'PUBLISHED',
        }}
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontWeight: 600, fontSize: 13 }}>
              Name <span style={{ color: '#e53e3e' }}>*</span>
            </label>
            <Input
              placeholder="e.g. Acme Corp HRIS Mapping v1"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </div>
          <div>
            <label style={{ fontWeight: 600, fontSize: 13 }}>Description</label>
            <Input.TextArea
              rows={2}
              placeholder="e.g. Initial mapping for payroll integration"
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
            />
          </div>
          <div>
            <label style={{ fontWeight: 600, fontSize: 13 }}>
              Change Summary
            </label>
            <Input.TextArea
              rows={2}
              placeholder="e.g. Added employee ID, name, status, and email mappings"
              value={changeSummary}
              onChange={(e) => setChangeSummary(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title="Import rules"
        open={importRulesModalOpen}
        onCancel={() => setImportRulesModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {hasExistingRulesToReplace ? (
            <Alert
              type="warning"
              showIcon
              message="Importing will replace all existing mapping rules and computed fields in this template."
            />
          ) : null}
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Import from file
            </div>
            <Button
              block
              icon={<ImportOutlined />}
              onClick={handleImportFromFileInModal}
            >
              Choose JSON file…
            </Button>
          </div>
          <Divider style={{ margin: 0 }}>or</Divider>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Import from another company&apos;s active template
            </div>
            <Select
              showSearch
              allowClear
              placeholder="Select company (active template with rules)"
              style={{ width: '100%' }}
              loading={isImportCompaniesLoading}
              options={importSelectOptions}
              value={selectedImportTemplateId}
              onChange={(v) => setSelectedImportTemplateId(v)}
              optionFilterProp="label"
            />
            <Button
              type="primary"
              block
              style={{ marginTop: 12 }}
              loading={importingFromCompany}
              disabled={!selectedImportTemplateId || isImportCompaniesLoading}
              onClick={() => void handleImportFromActiveCompanyTemplate()}
            >
              Import from selected company
            </Button>
          </div>
        </Space>
      </Modal>
    </Page>
  )
}
